if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.inlineVideoObserver = new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting && this.canPlayInlineVideo(target)) {
              this.playInlineVideo(target);
            } else {
              target.pause();
              target.closest('.slg-product-video').dataset.videoState = 'poster';
            }
          });
        }, { threshold: 0.25 });
        this.boundInlineVideos = new WeakSet();
        this.videoProgress = new WeakMap();
        this.videoProgressTimer = null;
        this.onVisibilityChange = () => this.refreshInlineVideos();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.bindInlineVideos();
        this.refreshInlineVideos();
        if (this.querySelector('.slg-product-video__element')) {
          this.videoProgressTimer = window.setInterval(() => this.checkInlineVideoProgress(), 500);
        }
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        if (this.mql.addEventListener) this.mql.addEventListener('change', this.onVisibilityChange);
        else this.mql.addListener(this.onVisibilityChange);
      }

      disconnectedCallback() {
        this.inlineVideoObserver.disconnect();
        window.clearInterval(this.videoProgressTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onVisibilityChange);
        else this.mql.removeListener(this.onVisibilityChange);
      }

      bindInlineVideos() {
        this.querySelectorAll('.slg-product-video__element').forEach((video) => {
          if (this.boundInlineVideos.has(video)) {
            this.inlineVideoObserver.observe(video);
            return;
          }
          this.boundInlineVideos.add(video);
          video.controls = false;
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.disablePictureInPicture = true;
          video.disableRemotePlayback = true;
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('disablepictureinpicture', '');
          video.setAttribute('disableremoteplayback', '');
          video.removeAttribute('controls');

          const wrapper = video.closest('.slg-product-video');
          video.addEventListener('playing', () => {
            this.videoProgress.set(video, { time: video.currentTime, at: performance.now() });
            wrapper.dataset.videoState = 'playing';
          });
          video.addEventListener('waiting', () => { if (!video.paused) wrapper.dataset.videoState = 'loading'; });
          video.addEventListener('stalled', () => { if (!video.paused) wrapper.dataset.videoState = 'loading'; });
          video.addEventListener('pause', () => { wrapper.dataset.videoState = 'poster'; });
          video.addEventListener('error', () => { wrapper.dataset.videoState = 'poster'; });
          this.inlineVideoObserver.observe(video);
        });
      }

      checkInlineVideoProgress() {
        const now = performance.now();
        this.querySelectorAll('.slg-product-video__element').forEach((video) => {
          if (video.paused || video.ended || !this.canPlayInlineVideo(video)) return;
          const previous = this.videoProgress.get(video);
          if (!previous) {
            this.videoProgress.set(video, { time: video.currentTime, at: now });
            return;
          }
          if (Math.abs(video.currentTime - previous.time) > 0.05) {
            this.videoProgress.set(video, { time: video.currentTime, at: now });
            video.closest('.slg-product-video').dataset.videoState = 'playing';
          } else if (now - previous.at >= 1500) {
            video.closest('.slg-product-video').dataset.videoState = 'loading';
          }
        });
      }

      canPlayInlineVideo(video) {
        if (document.hidden || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
        const rect = video.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0 || rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) return false;
        const item = video.closest('.product__media-item');
        if (!item) return false;
        return item.classList.contains('is-active') ||
          (this.mql.matches && !this.dataset.desktopLayout.includes('thumbnail'));
      }

      playInlineVideo(video) {
        if (!this.canPlayInlineVideo(video)) return;
        const wrapper = video.closest('.slg-product-video');
        if (!video.paused) return;
        this.videoProgress.set(video, { time: video.currentTime, at: performance.now() });
        wrapper.dataset.videoState = 'loading';
        const result = video.play();
        if (result?.catch) result.catch(() => { if (video.paused) wrapper.dataset.videoState = 'poster'; });
      }

      refreshInlineVideos() {
        this.bindInlineVideos();
        this.querySelectorAll('.slg-product-video__element').forEach((video) => {
          if (this.canPlayInlineVideo(video)) this.playInlineVideo(video);
          else {
            video.pause();
            video.closest('.slg-product-video').dataset.videoState = 'poster';
          }
        });
      }

      onSlideChanged(event) {
        const alreadyActive = event.detail.currentElement.classList.contains('is-active');
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.toggle('is-active', item === event.detail.currentElement);
        });
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        if (!alreadyActive) this.playActiveMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        this.bindInlineVideos();
        this.refreshInlineVideos();
        const inlineVideo = activeItem.querySelector('.slg-product-video__element');
        if (inlineVideo) {
          this.playInlineVideo(inlineVideo);
          return;
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
